import { useEffect, useRef, useState } from 'react';
import { useDebouncedInput } from '@/hooks/useDebouncedInput';
import SearchIcon from '@/assets/icons/search.svg?react';
import styles from './SearchBar.module.scss';
import { useClickOutside } from '@/hooks/useClickOutside';
import PlayerTable from './PlayerTable';
import { PlayerForSaveDto } from '@/api/models';
import { PlayerService } from '@/service';
import { AppSettingsDB } from '@/db/repositories';
import { useActivePlayer } from '@/context/ActivePlayerContext';

interface ISearchBarProps {
  placeholder: string;
  /* eslint-disable no-unused-vars */
  selectPlayer: (selectedPlayer: PlayerForSaveDto | null) => void;
  /* eslint-enable no-unused-vars */
  classNames?: string;
}

function SearchBar({ placeholder, classNames, selectPlayer }: ISearchBarProps) {
  const { player: activePlayer } = useActivePlayer();
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebouncedInput(query, 300);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [showPlayerTable, setShowPlayerTable] = useState(false);
  const [players, setPlayers] = useState([] as PlayerForSaveDto[]);
  const [nameQuery, setNameQuery] = useState('');
  const [selectedAccount, setSelectedAccount] = useState('');

  useEffect(() => {
    const asyncProcess = async () => {
      const appSettings = await AppSettingsDB.getAll();
      const accounts = appSettings['accounts'] || null;
      setSelectedAccount(accounts || '');
    };
    asyncProcess();
  }, [selectPlayer, selectedAccount]);

  useEffect(() => {
    setNameQuery(debouncedQuery);
    if (debouncedQuery == '') {
      selectPlayer(null);
    }
  }, [debouncedQuery, setNameQuery]);

  //can't use the fetchPlayer() in useTourBag b/c the My Players checkbox value can't be used on this page. So have to repeat the function here
  useEffect(() => {
    const fetchPlayers = async () => {
      const { players } = await PlayerService.queryPlayersWithOffset({
        nameQuery,
        selectedAccount,
        myPlayerIds: null,
        offset: 0,
        pageSize: 100,
      });
      setPlayers(players);
      //    setTotal(total);
    };
    fetchPlayers();
  }, [nameQuery]);

  useClickOutside(wrapperRef, 1, () => {
    setShowPlayerTable(false);
  });

  const handleSelectPlayer = (selectedPlayer: PlayerForSaveDto) => {
    setShowPlayerTable(false);
    setQuery(selectedPlayer.firstName + ' ' + selectedPlayer.lastName);
    selectPlayer(selectedPlayer);
  };

  const onFocus = () => {
    setShowPlayerTable(true);
  };

  const handleClearButton = () => {
    setQuery('');
    setShowPlayerTable(true);
    selectPlayer(null);
  };

  const availablePlayers = players.filter((player) => player.playerId !== activePlayer?.playerId);

  return (
    <div ref={wrapperRef}>
      <div className={`${styles.searchContainer} ${classNames}`}>
        <SearchIcon className={styles.icon} />
        <input type="text" placeholder={placeholder || 'Search'} value={query} onFocus={onFocus} onChange={(e) => setQuery(e.target.value)} className={styles.input} />
        {query && (
          <button type="button" className={styles.clearButton} onClick={handleClearButton}>
            ×
          </button>
        )}
      </div>
      {showPlayerTable && (
        <div>
          <PlayerTable players={availablePlayers} selectPlayer={handleSelectPlayer} />
        </div>
      )}
    </div>
  );
}

export default SearchBar;
